import React from 'react';
import { Outlet, useNavigate, useParams } from '@tanstack/react-router';
import { House } from '@phosphor-icons/react';
import { Spinner } from '@chatsdet/ui';
import { useQuery } from '@tanstack/react-query';
import { useSubNav } from '../../hooks/useSubNav';
import type { SubNavConfig } from '../../contexts/SubNavContext';
import { fetchPageProperties, type PagePropertyItem } from './pagesApi';

function usePageProperties() {
  return useQuery({
    queryKey: ['page-properties'],
    queryFn: fetchPageProperties,
  });
}

export function PagesLayout() {
  const { data: properties, isLoading } = usePageProperties();
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as { propertyId?: string };

  const navItems = React.useMemo<SubNavConfig['items']>(() => {
    if (!properties || properties.length === 0) return [];
    return properties.map((p: PagePropertyItem) => ({
      section: 'Applications',
      id: p.id,
      label: p.name,
      icon: <House size={16} />,
      to: `/pages/${p.id}`,
    }));
  }, [properties]);

  const navConfig = React.useMemo<SubNavConfig>(() => {
    return {
      title: 'Pages',
      basePath: '/pages',
      items: navItems,
      getActiveId: ({ pathname }) => {
        if (!pathname.startsWith('/pages')) return undefined;
        const match = pathname.match(/^\/pages\/([^/]+)/);
        return match?.[1];
      },
    };
  }, [navItems]);

  useSubNav(navConfig);

  // Auto-redirect to first property if none selected
  React.useEffect(() => {
    if (!params.propertyId && properties && properties.length > 0) {
      navigate({ to: '/pages/$propertyId', params: { propertyId: properties[0]!.id } });
    }
  }, [params.propertyId, properties, navigate]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner />
      </div>
    );
  }

  return <Outlet />;
}
